import isEqual from 'lodash-es/isEqual'
import uniqueId from 'lodash-es/uniqueId'
import * as Yup from 'yup'

import { EMPTY_LIST } from '../../../constants/common'
import { getItemsIds } from '../../../utils/common'
import { toBuildingAddressSelectItem } from '../../Building'
import { toCategorySelectItem } from '../../Category'
import { formValidationSchemaNoChanged } from '../../Form'

import {
  PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_FIELD,
  PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_BUILDINGS_FIELD,
  PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_CATEGORIES_FIELD,
  PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_UNIQUE_ID_FIELD,
  PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_UNIQUE_ID_PREFIX,
} from './EditBuildingAndCategoryModal.constants'

function getMappingRowWithUniqueId(row) {
  return {
    [PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_UNIQUE_ID_FIELD]: uniqueId(
      PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_UNIQUE_ID_PREFIX
    ),
    ...row,
  }
}

function getMappingRowsIdsForCompare(mappingRows) {
  return mappingRows.map(row => ({
    [PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_BUILDINGS_FIELD]:
      getItemsIds(
        row?.[
          PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_BUILDINGS_FIELD
        ] ?? EMPTY_LIST,
        'value'
      ),
    [PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_CATEGORIES_FIELD]:
      getItemsIds(
        row?.[
          PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_CATEGORIES_FIELD
        ] ?? EMPTY_LIST,
        'value'
      ),
  }))
}

function mapCategoriesToRows(categoriesObjects = EMPTY_LIST) {
  if (categoriesObjects.length !== 0) {
    return categoriesObjects.map(categoryObject =>
      getMappingRowWithUniqueId({
        [PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_BUILDINGS_FIELD]: (
          categoryObject?.buildings ?? EMPTY_LIST
        ).map(toBuildingAddressSelectItem),
        [PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_CATEGORIES_FIELD]:
          categoryObject?.id
            ? [toCategorySelectItem(categoryObject)]
            : EMPTY_LIST,
      })
    )
  }

  return EMPTY_LIST
}

function mapBuildingsWithoutCategoryToRows({
  buildingsObjects = EMPTY_LIST,
  mapping = EMPTY_LIST,
}) {
  const buildingById = new Map(
    buildingsObjects.map(building => [building.id, building])
  )

  const mappingsWithoutCategory = mapping.filter(
    mapItem => mapItem?.category == null && mapItem?.building != null
  )

  if (mappingsWithoutCategory.length !== 0) {
    return mappingsWithoutCategory
      .map(mapItem => {
        const buildingObject = buildingById.get(mapItem.building)

        if (!buildingObject) {
          return null
        }

        return getMappingRowWithUniqueId({
          [PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_BUILDINGS_FIELD]: [
            toBuildingAddressSelectItem(buildingObject),
          ],
          [PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_CATEGORIES_FIELD]:
            EMPTY_LIST,
        })
      })
      .filter(Boolean)
  }

  return EMPTY_LIST
}

export function isEditBuildingAndCategoryMappingsEqual({
  initialValues,
  values,
}) {
  const initialRows =
    initialValues?.[PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_FIELD] ??
    EMPTY_LIST

  const currentRows =
    values?.[PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_FIELD] ?? EMPTY_LIST

  return isEqual(
    getMappingRowsIdsForCompare(initialRows),
    getMappingRowsIdsForCompare(currentRows)
  )
}

export function getValidationSchema({ initialValues, isItemAction }) {
  return Yup.object({
    [PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_FIELD]: Yup.array().of(
      Yup.object({
        [PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_BUILDINGS_FIELD]:
          Yup.array().nullable(),
        [PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_CATEGORIES_FIELD]:
          Yup.array().nullable(),
      })
    ),
  }).when(EMPTY_LIST, {
    is: () => isItemAction,
    then: schema =>
      schema.concat(
        formValidationSchemaNoChanged({
          initialValues,
          isEqual: isEditBuildingAndCategoryMappingsEqual,
        })
      ),
  })
}

export function createEmptyMappingRow() {
  return {
    [PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_BUILDINGS_FIELD]:
      EMPTY_LIST,
    [PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_CATEGORIES_FIELD]:
      EMPTY_LIST,
    [PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_UNIQUE_ID_FIELD]: uniqueId(
      PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_UNIQUE_ID_PREFIX
    ),
  }
}

export function getInitialValues(initialData) {
  if (!initialData) {
    return {
      [PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_FIELD]: [
        createEmptyMappingRow(),
      ],
    }
  }

  const { buildingsObjects, categoriesObjects, mapping } = initialData

  const categoryRows = mapCategoriesToRows(categoriesObjects)

  const buildingWithoutCategoryRows = mapBuildingsWithoutCategoryToRows({
    buildingsObjects,
    mapping,
  })

  const rows = [...categoryRows, ...buildingWithoutCategoryRows]

  return {
    [PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_FIELD]:
      rows.length > 0 ? rows : [createEmptyMappingRow()],
  }
}

export function getCanAddMappingRow(lastRow) {
  const buildingCount =
    lastRow?.[PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_BUILDINGS_FIELD]
      ?.length ?? 0

  const categoryCount =
    lastRow?.[PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_CATEGORIES_FIELD]
      ?.length ?? 0

  return buildingCount > 0 || categoryCount > 0
}

export function getQaNameMappingRowDeleteButton(name, index) {
  return `${name}_${index + 1}`
}

export function getMappingRowKey({ index, row }) {
  const rowUniqueId =
    row?.[PROVIDER_EDIT_BUILDING_AND_CATEGORY_MAPPING_ROW_UNIQUE_ID_FIELD] ??
    index

  return `${rowUniqueId}_${index}`
}
